import { useState, useEffect } from "react";
import { images } from "../constants/images";
import "../assets/stylesheets/bootstrap.min.css";
import "../assets/stylesheets/style.css";
import "../assets/stylesheets/responsive.css";

export default function Services() {
  useEffect(() => {
    document.title = "Our Services - Custom Software Development | Plural Code";
    window.scrollTo(0, 0);
  }, []);

  // All services data
  const services = [
    {
      id: 1,
      icon: "🏢",
      title: "Custom Business Software Development",
      slug: "custom-business-software",
      shortDesc: "Software built around YOUR workflows, not generic templates",
      description:
        "Stop forcing your business into one-size-fits-all software. We build custom solutions that match your exact processes, giving you a competitive advantage through better operations.",
      benefits: [
        "Built for your exact workflows",
        "No unnecessary features",
        "You own 100% of the code",
        "Scales with your business",
        "No vendor lock-in",
      ],
      idealFor: [
        "Mid-sized businesses (50-200+ employees)",
        "Companies with unique processes",
        "Businesses outgrowing current software",
        "Organizations needing competitive edge",
      ],
      investment: "From $25,000",
      timeline: "3-6 months",
      image: images?.customBusinessSoftware,
      featured: true,
      gradient: "linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%)",
    },
    {
      id: 2,
      icon: "🌐",
      title: "Web Application Development",
      slug: "web-application-development",
      shortDesc: "Powerful web platforms that work across all devices",
      description:
        "Build custom web applications for your customers, employees, or partners. From customer portals to SaaS platforms to internal management tools we create web apps that deliver value.",
      benefits: [
        "Works on any device/browser",
        "No app store approval needed",
        "Instant updates for all users",
        "Lower maintenance costs",
        "Easy to scale",
      ],
      idealFor: [
        "Customer-facing portals",
        "Internal management systems",
        "B2B/B2C platforms",
        "SaaS products",
        "Data dashboards",
      ],
      investment: "From $15,000",
      timeline: "2-5 months",
      image: images?.webApp,
      featured: true,
      gradient: "linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%)",
    },
    {
      id: 3,
      icon: "📱",
      title: "Mobile App Development",
      slug: "mobile-application-development",
      shortDesc: "Native iOS & Android apps your users will love",
      description:
        "Take your business mobile with custom apps for iOS and Android. We build native or cross-platform apps that provide seamless user experiences and work offline when needed.",
      benefits: [
        "Native performance",
        "Offline functionality",
        "Push notifications",
        "Device feature access",
        "App store presence",
      ],
      idealFor: [
        "Field service teams",
        "Customer engagement apps",
        "Data collection in field",
        "IoT device control",
        "B2C mobile experiences",
      ],
      investment: "From $20,000",
      timeline: "3-6 months",
      image: images?.mobileApp,
      featured: true,
      gradient: "linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%)",
    },

    {
      id: 5,
      icon: "🛠️",
      title: "Ongoing Support & Maintenance",
      slug: "support-and-maintenance",
      shortDesc: "Keep your software secure, fast, and evolving",
      description:
        "Software isn't 'done' after launch. Our ongoing support keeps your systems secure, performant, and aligned with your evolving business needs without needing in-house developers.",
      benefits: [
        "Security updates & patches",
        "Performance monitoring",
        "Bug fixes",
        "Feature enhancements",
        "Technical support",
      ],
      idealFor: [
        "Existing custom software",
        "Mission-critical systems",
        "Businesses without in-house devs",
        "Software needing evolution",
        "Compliance requirements",
      ],
      investment: "From $800/month",
      timeline: "Ongoing",
      image: images?.serviceSupport || images?.aboutUs,
      featured: false,
      gradient: "linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%)",
    },
  ];

  const featuredServices = services.filter((s) => s.featured);

  return (
    <div className="services-page">
      {/* HERO SECTION - Same style as home page */}
      <section className="services-hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-badge">
              <span className="badge-icon">🚀</span>
              <span className="badge-text">OUR SERVICES</span>
            </div>
            <h1 className="hero-title">
              Custom Software Development{" "}
              <span className="highlight">Services</span>
            </h1>
            <p className="hero-subtitle">
              We build custom software, web applications, mobile apps, and
              integrations that fit your business perfectly not the other way
              around.
            </p>
            <div className="hero-stats">
              <div className="stat-item">
                <div className="stat-value">200+</div>
                <div className="stat-label" style={{ fontWeight: "bold" }}>
                  Projects Delivered
                </div>
              </div>
              <div className="stat-item">
                <div className="stat-value">13+</div>
                <div className="stat-label" style={{ fontWeight: "bold" }}>
                  Years Experience
                </div>
              </div>
              <div className="stat-item">
                <div className="stat-value">95%</div>
                <div className="stat-label" style={{ fontWeight: "bold" }}>
                  Client Retention
                </div>
              </div>
              <div className="stat-item">
                <div className="stat-value">6</div>
                <div className="stat-label" style={{ fontWeight: "bold" }}>
                  Countries Served
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CUSTOM SOFTWARE */}
      <section className="why-custom-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">WHY CUSTOM?</span>
            <h2 className="section-title">Why Choose Custom Software?</h2>
            <p className="section-subtitle">
              Off-the-shelf software forces you to adapt your business to their
              workflows. Custom software adapts to yours.
            </p>
          </div>

          <div className="benefits-grid">
            {[
              {
                icon: "🎯",
                title: "Perfect Fit",
                description: (
                  <>
                    Tailored to your workflows,
                    <br />
                    not generic systems
                  </>
                ),
              },
              {
                icon: "⚡",
                title: "Innovation Edge",
                description:
                  "Do things competitors can't with off-the-shelf tools",
              },
              {
                icon: "💰",
                title: "Long-Term Value",
                description: "Pay once, own forever. No endless subscriptions",
              },
              {
                icon: "📈",
                title: "Scales With You",
                description: "Grow without hitting artificial limitations",
              },
            ].map((benefit, index) => (
              <div className="benefit-card" key={index}>
                <div className="benefit-icon">{benefit.icon}</div>
                <h3 className="benefit-title">{benefit.title}</h3>
                <p className="benefit-description">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED SERVICES */}
      <section className="featured-services-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">CORE SERVICES</span>
            <h2 className="section-title">Our Core Services</h2>
            <p className="section-subtitle">
              Comprehensive solutions tailored to your business needs
            </p>
          </div>

          <div className="featured-services-grid">
            {featuredServices.map((service, index) => (
              <div className="featured-service-card" key={service.id}>
                <div
                  className="service-image"
                  style={{
                    backgroundImage: `url(${service.image})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  <div className="image-overlay"></div>
                  <div className="service-icon">{service.icon}</div>
                </div>
                <div className="service-content">
                  <div className="service-header">
                    <h3 className="service-title">{service.title}</h3>
                    <p className="service-short-desc">{service.shortDesc}</p>
                  </div>

                  <p className="service-description">{service.description}</p>

                  <div className="service-benefits">
                    <h4>Key Benefits:</h4>
                    <div className="benefits-list">
                      {service.benefits.map((benefit, i) => (
                        <div className="benefit-item" key={i}>
                          <span className="benefit-check">✓</span>
                          <span>{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="service-details">
                    <div className="investment">
                      <span className="detail-label">Investment:</span>
                      <span className="detail-value highlight">
                        {service.investment}
                      </span>
                    </div>
                    <div className="timeline">
                      <span className="detail-label">Timeline:</span>
                      <span className="detail-value">{service.timeline}</span>
                    </div>
                  </div>

                  <button
                    onClick={() =>
                      window.open(`/services/${service.slug}`, "_blank")
                    }
                    className="service-cta"
                    style={{ background: service.gradient }}
                  >
                    <span>Learn More About This Service</span>
                    <span className="cta-arrow">→</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ALL SERVICES GRID */}
      <section className="all-services-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">COMPLETE OFFERINGS</span>
            <h2 className="section-title">Complete Service Offerings</h2>
            <p className="section-subtitle">
              Choose the perfect solution for your business needs
            </p>
          </div>

          <div className="all-services-grid">
            {services.map((service) => (
              <div
                className={`service-card ${service.featured ? "featured" : ""}`}
                key={service.id}
                onClick={() =>
                  window.open(`/services/${service.slug}`, "_blank")
                }
              >
                <div
                  className="card-header"
                  // style={{ background: service.gradient }}
                  style={{
                    background: `
                      linear-gradient(
                        rgba(10, 36, 88, 0.85),
                        rgba(10, 36, 88, 0.85)
                      ),
                      url(${service?.image})
                    `,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                  }}
                >
                  <div className="card-icon">{service.icon}</div>
                  {service.featured && (
                    <div className="featured-badge">
                      <span>⭐ POPULAR</span>
                    </div>
                  )}
                </div>
                <div className="card-body">
                  <h3 className="card-title">{service.title}</h3>
                  <p className="card-description">{service.description}</p>

                  <div className="card-benefits">
                    <h4>Key Benefits:</h4>
                    <div className="benefits-list">
                      {service.benefits.map((benefit, i) => (
                        <div className="benefit-item" key={i}>
                          <span className="benefit-check">✓</span>
                          <span>{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="card-ideal-for">
                    <h4>Ideal For:</h4>
                    <div className="ideal-for-list">
                      {service.idealFor.map((item, i) => (
                        <div className="ideal-item" key={i}>
                          <span>• {item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* <div className="card-footer">
                  <div className="investment-info">
                    <span className="investment-label">Starting from</span>
                    <span
                      className="investment-value"
                      style={{
                        color: service.gradient.includes("0a2458")
                          ? "#0a2458"
                          : "#1a3b8a",
                      }}
                    >
                      {service.investment}
                    </span>
                  </div>
                  <div className="learn-more">
                    <span>Learn More</span>
                    <span className="arrow">→</span>
                  </div>
                </div> */}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW WE WORK */}
      <section className="industries-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">INDUSTRIES</span>
            <h2 className="section-title">Industries We Serve</h2>
            <p className="section-subtitle">
              Expertise across diverse sectors and business models
            </p>
          </div>

          <div className="industries-grid">
            {[
              { icon: "🏭", name: "Manufacturing" },
              { icon: "🏥", name: "Healthcare" },
              { icon: "🚚", name: "Logistics & Supply Chain" },
              { icon: "🏪", name: "Retail & E-commerce" },
              { icon: "💼", name: "Professional Services" },
              { icon: "🏦", name: "Finance & Insurance" },
              { icon: "🏗️", name: "Construction" },
              { icon: "🍽️", name: "Hospitality" },
              { icon: "🏠", name: "Real Estate" },
              { icon: "🎓", name: "Education" },
              { icon: "💪", name: "Fitness & Wellness" },
              { icon: "🎨", name: "Creative Services" },
            ].map((industry, index) => (
              <div className="industry-card" key={index}>
                <div className="industry-icon">{industry.icon}</div>
                <h4 className="industry-name">{industry.name}</h4>
              </div>
            ))}
          </div>

          <div className="industries-note">
            <p>
              Don't see your industry? We adapt. <br />
              <br />
              <div className="testimonials-cta">
                <button
                  onClick={() =>
                    window.open(
                      "https://topmate.io/pluralcode/1677658",
                      "_blank",
                    )
                  }
                  className="view-case-studies"
                >
                  Talk to an Expert →
                </button>
              </div>
            </p>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="why-choose-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">WHY CHOOSE US</span>
            <h2 className="section-title">Why Choose Plural Code?</h2>
            <p className="section-subtitle">
              What sets us apart in delivering exceptional custom software
              solutions
            </p>
          </div>

          <div className="advantages-grid">
            {[
              {
                icon: "🎯",
                title: "Business-First Approach",
                description:
                  "We care about your ROI, not just shipping code. Every feature is evaluated for business value.",
              },
              {
                icon: "💬",
                title: "Transparent Communication",
                description:
                  "No jargon, no surprises. You always know what's happening, what's next, and why.",
              },
              {
                icon: "🔑",
                title: "You Own Everything",
                description:
                  "Complete code ownership, no licensing fees, no vendor lock-in. Your software, your rules.",
              },
              {
                icon: "🌏",
                title: "International Experience",
                description:
                  "We've built software for clients across US, Australia, New Zealand, Singapore, Canada, and beyond.",
              },
              {
                icon: "⚡",
                title: "Proven Track Record",
                description:
                  "Since 2015, 100+ successful projects delivering measurable business impact.",
              },
              {
                icon: "🤝",
                title: "Long-Term Partnership",
                description:
                  "We don't disappear after launch. Ongoing support, evolution, strategic guidance.",
              },
            ].map((advantage, index) => (
              <div className="advantage-card" key={index}>
                <div className="advantage-icon">{advantage.icon}</div>
                <div className="advantage-content">
                  <h3 className="advantage-title">{advantage.title}</h3>
                  <p className="advantage-description">
                    {advantage.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="testimonials-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">CLIENT STORIES</span>
            <h2 className="section-title">What Our Clients Say</h2>
            <p className="section-subtitle">
              Real results from businesses that transformed with our custom
              solutions
            </p>
          </div>

          <div className="row">
            {[
              {
                quote:
                  "This software transformed how we operate. Before, we were reactive. Now, we see issues forming and fix them proactively. The system was built around our actual process.",
                author: "Operations Director",
                company: "Australian Manufacturing Company",
                service: "Custom Business Software",
                rating: 5,
              },
              {
                quote:
                  "Clients love the transparency and 24/7 access. We look like a much bigger firm now. The portal has become a key differentiator for us.",
                author: "Managing Partner",
                company: "Professional Services Firm, Singapore",
                service: "Web Application",
                rating: 5,
              },
              {
                quote:
                  "Technicians love it easier than paperwork. Office team loves it no more data entry. Customers love it instant updates. Win-win-win.",
                author: "Operations Manager",
                company: "Logistics Company, New Zealand",
                service: "Mobile App",
                rating: 5,
              },
            ].map((testimonial, index) => (
              <div className="col-lg-4 col-md-6 col-sm-12 mb-4">
                <div className="testimonial-card" key={index}>
                  <div className="testimonial-rating">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <span key={i} className="star">
                        ★
                      </span>
                    ))}
                  </div>
                  <p className="testimonial-quote">{testimonial.quote}</p>
                  <div className="testimonial-footer">
                    <div className="testimonial-author">
                      <div className="author-avatar">
                        {testimonial.author.charAt(0)}
                      </div>
                      <div className="author-info">
                        <h4 className="author-name">{testimonial.author}</h4>
                        <p className="author-company">{testimonial.company}</p>
                      </div>
                    </div>
                    <span className="testimonial-service">
                      {testimonial.service}
                    </span>
                  </div>
                </div>
                {/* <br /> */}
              </div>
            ))}
          </div>

          {/* <div className="testimonials-cta">
            <button
              onClick={() => window.open("/work", "_blank")}
              className="view-case-studies"
            >
              View All Case Studies →
            </button>
          </div> */}
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="services-cta">
        <div className="container">
          <div className="cta-content">
            <div className="cta-badge">
              <span className="badge-icon">✨</span>
              <span className="badge-text" style={{ color: "white" }}>
                GET STARTED
              </span>
            </div>
            <h2 className="cta-title">
              Ready to Build Software That Actually Fits?
            </h2>
            <p className="cta-subtitle">
              Let's have an honest conversation about your challenges, your
              goals, and whether custom software is the right solution for you.
            </p>

            <div className="cta-buttons">
              <button
                onClick={() =>
                  window.open("https://topmate.io/pluralcode/1677658", "_blank")
                }
                className="cta-primary"
              >
                <span>Schedule Free Consultation</span>
                <span className="cta-arrow">→</span>
              </button>
              {/* <button
                onClick={() => window.open("/work", "_blank")}
                className="cta-secondary"
              >
                <span>View Our Work</span>
                <span className="cta-arrow">→</span>
              </button> */}
            </div>

            <div className="cta-contact">
              <div className="contact-info">
                <div className="contact-item">
                  <span className="contact-icon">📧</span>
                  <span className="contact-text">info@pluralcode.com</span>
                </div>
                <div className="contact-item">
                  <span className="contact-icon">📞</span>
                  <span className="contact-text">+91 99589 20231</span>
                </div>
                <div className="contact-item">
                  <span className="contact-icon">🌏</span>
                  <span className="contact-text">
                    Serving businesses of all sizes and industries around the
                    world.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style jsx>{`
        .services-page {
          font-family:
            -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          overflow-x: hidden;
        }

        /* Common Styles */
        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
          width: 100%;
          box-sizing: border-box;
        }

        .section-header {
          text-align: center;
          margin-bottom: 60px;
        }

        .section-badge {
          display: inline-block;
          padding: 8px 20px;
          background: rgba(10, 36, 88, 0.1);
          color: #0a2458;
          font-weight: 600;
          font-size: 14px;
          border-radius: 50px;
          letter-spacing: 1px;
          margin-bottom: 20px;
        }

        .section-title {
          font-size: clamp(2rem, 5vw, 2.75rem);
          color: #0a2458;
          margin-bottom: 16px;
          font-weight: 700;
          line-height: 1.2;
        }

        .section-subtitle {
          font-size: 1.125rem;
          color: #0a2458;
          max-width: 700px;
          margin: 0 auto;
          line-height: 1.6;
        }

        .highlight {
          background: linear-gradient(90deg, #ffd700 0%, #ffec8b 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .highlight-link {
          color: #0a2458;
          font-weight: 600;
          text-decoration: none;
          position: relative;
        }

        .highlight-link::after {
          content: "";
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 100%;
          height: 2px;
          background: #ffd700;
          transform: scaleX(0);
          transition: transform 0.3s ease;
        }

        .highlight-link:hover::after {
          transform: scaleX(1);
        }

        /* Hero Section */
        .services-hero {
          background: linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%);
          color: white;
          padding: 100px 0 80px;
          position: relative;
          overflow: hidden;
        }

        .services-hero::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background:
            radial-gradient(
              circle at 20% 80%,
              rgba(255, 215, 0, 0.1) 0%,
              transparent 50%
            ),
            radial-gradient(
              circle at 80% 20%,
              rgba(255, 255, 255, 0.05) 0%,
              transparent 50%
            );
        }

        .hero-content {
          max-width: 900px;
          margin: 0 auto;
          text-align: center;
          position: relative;
          z-index: 1;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          padding: 8px 20px;
          border-radius: 50px;
          margin-bottom: 24px;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .badge-icon {
          font-size: 20px;
        }

        .badge-text {
          font-size: 14px;
          font-weight: 600;
          letter-spacing: 1px;
        }

        .hero-title {
          font-size: clamp(2.5rem, 5vw, 3.5rem);
          font-weight: 700;
          line-height: 1.1;
          margin-bottom: 20px;
        }

        .hero-subtitle {
          font-size: 1.25rem;
          line-height: 1.6;
          color: rgba(255, 255, 255, 0.9);
          max-width: 700px;
          margin: 0 auto 40px;
        }

        .hero-stats {
          display: flex;
          justify-content: center;
          gap: 40px;
          flex-wrap: wrap;
        }

        .stat-item {
          text-align: center;
          min-width: 120px;
        }

        .stat-value {
          font-size: 2.5rem;
          font-weight: 700;
          color: #ffd700;
          margin-bottom: 4px;
        }

        .stat-label {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.8);
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        /* Why Custom Section */
        .why-custom-section {
          padding: 100px 0;
          background: linear-gradient(180deg, #ffffff 0%, #f8faff 100%);
        }

        .benefits-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 30px;
          max-width: 1100px;
          margin: 0 auto;
        }

        .benefit-card {
          background: white;
          border-radius: 20px;
          padding: 40px 30px;
          text-align: center;
          box-shadow: 0 10px 40px rgba(10, 36, 88, 0.08);
          transition: all 0.3s ease;
        }

        .benefit-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 60px rgba(10, 36, 88, 0.15);
        }

        .benefit-icon {
          font-size: 64px;
          margin-bottom: 20px;
        }

        .benefit-title {
          font-size: 1.5rem;
          color: #0a2458;
          margin-bottom: 12px;
          font-weight: 600;
        }

        .benefit-description {
          color: #0a2458;
          line-height: 1.6;
          margin: 0;
        }

        /* Featured Services */
        .featured-services-section {
          padding: 100px 0;
          background: #ffffff;
        }

        .featured-services-grid {
          display: flex;
          flex-direction: column;
          gap: 60px;
          max-width: 1100px;
          margin: 0 auto;
        }

        .featured-service-card {
          background: white;
          border-radius: 24px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(10, 36, 88, 0.1);
          transition: transform 0.3s ease;
        }

        .featured-service-card:hover {
          transform: translateY(-5px);
        }

        .service-image {
          height: 300px;
          position: relative;
        }

        .image-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            to bottom,
            rgba(10, 36, 88, 0.7),
            rgba(10, 36, 88, 0.3)
          );
        }

        .service-icon {
          position: absolute;
          top: 30px;
          right: 30px;
          font-size: 60px;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          width: 100px;
          height: 100px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .service-content {
          padding: 40px;
        }

        .service-header {
          margin-bottom: 24px;
        }

        .service-title {
          font-size: 2rem;
          color: #0a2458;
          margin-bottom: 12px;
          font-weight: 700;
        }

        .service-short-desc {
          font-size: 1.125rem;
          color: #1a3b8a;
          font-weight: 600;
          margin: 0;
        }

        .service-description {
          font-size: 1.125rem;
          color: #0a2458;
          line-height: 1.6;
          margin-bottom: 32px;
        }

        .service-benefits h4 {
          color: #0a2458;
          font-size: 1.125rem;
          margin-bottom: 16px;
          font-weight: 600;
        }

        .benefits-list {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 12px;
          margin-bottom: 32px;
        }

        .benefit-item {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .benefit-check {
          color: #10b981;
          font-weight: bold;
          font-size: 1.25rem;
        }

        .service-details {
          display: flex;
          gap: 40px;
          margin-bottom: 32px;
          padding: 20px;
          background: #f8fafc;
          border-radius: 12px;
        }

        .investment,
        .timeline {
          display: flex;
          flex-direction: column;
        }

        .detail-label {
          font-size: 0.875rem;
          color: #0a2458;
          margin-bottom: 4px;
        }

        .detail-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #0a2458;
        }

        .service-cta {
          background: linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%);
          color: white;
          border: none;
          padding: 18px 36px;
          font-size: 1.125rem;
          font-weight: 600;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .service-cta:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(10, 36, 88, 0.3);
        }

        .cta-arrow {
          font-size: 1.5rem;
          transition: transform 0.3s ease;
        }

        .service-cta:hover .cta-arrow {
          transform: translateX(5px);
        }

        /* All Services Grid */
        .all-services-section {
          padding: 100px 0;
          background: linear-gradient(180deg, #ffffff 0%, #f8faff 100%);
        }

        .all-services-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
          gap: 30px;
          max-width: 1100px;
          margin: 0 auto;
        }

        @media (max-width: 768px) {
          .all-services-grid {
            grid-template-columns: 1fr;
          }
        }

        .service-card {
          background: white;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 10px 40px rgba(10, 36, 88, 0.08);
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .service-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 60px rgba(10, 36, 88, 0.15);
        }

        .service-card.featured {
          border: 3px solid #ffd700;
        }

        .card-header {
          padding: 30px;
          color: white;
          position: relative;
        }

        .card-icon {
          font-size: 64px;
          margin-bottom: 20px;
        }

        .featured-badge {
          position: absolute;
          top: 20px;
          right: 20px;
          background: #ffd700;
          color: #0a2458;
          padding: 6px 15px;
          border-radius: 50px;
          font-size: 12px;
          font-weight: 600;
        }

        .card-body {
          padding: 30px;
        }

        .card-title {
          font-size: 1.5rem;
          color: #0a2458;
          margin-bottom: 16px;
          font-weight: 700;
        }

        .card-description {
          color: #0a2458;
          line-height: 1.6;
          margin-bottom: 24px;
        }

        .card-benefits h4,
        .card-ideal-for h4 {
          color: #0a2458;
          font-size: 1rem;
          margin-bottom: 12px;
          font-weight: 600;
        }

        .ideal-for-list {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 8px;
        }

        .ideal-item {
          color: #0a2458;
          font-size: 0.875rem;
        }

        .card-footer {
          padding: 20px 30px;
          background: #f8fafc;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .investment-info {
          display: flex;
          flex-direction: column;
        }

        .investment-label {
          font-size: 0.75rem;
          color: #0a2458;
          margin-bottom: 4px;
        }

        .investment-value {
          font-size: 1.5rem;
          font-weight: 700;
        }

        .learn-more {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #0a2458;
          font-weight: 600;
        }

        .learn-more .arrow {
          transition: transform 0.3s ease;
        }

        .service-card:hover .learn-more .arrow {
          transform: translateX(5px);
        }

        /* Process Section */
        .process-section {
          padding: 100px 0;
          background: #ffffff;
        }

        .process-steps {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 40px;
          max-width: 1100px;
          margin: 0 auto;
          position: relative;
        }

        .process-step {
          position: relative;
          text-align: center;
        }

        .step-number {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          font-weight: 700;
          margin: 0 auto 24px;
          box-shadow: 0 10px 30px rgba(10, 36, 88, 0.2);
        }

        .step-content {
          padding: 20px;
        }

        .step-title {
          font-size: 1.5rem;
          color: #0a2458;
          margin-bottom: 12px;
          font-weight: 600;
        }

        .step-description {
          color: #0a2458;
          line-height: 1.6;
          margin: 0;
        }

        .step-connector {
          position: absolute;
          top: 40px;
          right: -20px;
          width: 40px;
          height: 2px;
          background: linear-gradient(to right, #1a3b8a, #e2e8f0);
        }

        @media (max-width: 768px) {
          .step-connector {
            display: none;
          }
        }

        /* Industries Section */
        .industries-section {
          padding: 100px 0;
          background: linear-gradient(180deg, #ffffff 0%, #f8faff 100%);
        }

        .industries-grid {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 20px;
          max-width: 1063px;
          margin: 0 auto 40px;
        }

        .industry-card {
          background: white;
          border-radius: 16px;
          padding: 30px 20px;
          text-align: center;
          transition: all 0.3s ease;
          border: 1px solid #e2e8f0;
        }

        .industry-card:hover {
          transform: translateY(-5px);
          border-color: #0a2458;
          box-shadow: 0 10px 30px rgba(10, 36, 88, 0.1);
        }

        .industry-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .industry-name {
          font-size: 1rem;
          color: #0a2458;
          font-weight: 600;
          margin: 0;
        }

        .industries-note {
          text-align: center;
          font-size: 1.125rem;
          color: #0a2458;
        }

        /* Why Choose Us */
        .why-choose-section {
          padding: 100px 0;
          background: #ffffff;
        }

        .advantages-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 30px;
          max-width: 1100px;
          margin: 0 auto;
        }

        .advantage-card {
          background: #f8fafc;
          border-radius: 20px;
          padding: 40px 30px;
          display: flex;
          align-items: flex-start;
          gap: 20px;
          transition: all 0.3s ease;
        }

        .advantage-card:hover {
          background: white;
          transform: translateY(-5px);
          box-shadow: 0 20px 60px rgba(10, 36, 88, 0.1);
        }

        .advantage-icon {
          font-size: 48px;
          flex-shrink: 0;
        }

        .advantage-content {
          flex: 1;
        }

        .advantage-title {
          font-size: 1.25rem;
          color: #0a2458;
          margin-bottom: 12px;
          font-weight: 600;
        }

        .advantage-description {
          color: #0a2458;
          line-height: 1.6;
          margin: 0;
        }

        /* Testimonials */
        .testimonials-section {
          padding: 100px 0;
          background: linear-gradient(180deg, #ffffff 0%, #f8faff 100%);
        }

        .testimonials-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 30px;
          max-width: 1100px;
          margin: 0 auto 60px;
        }

        .testimonial-card {
          background: white;
          border-radius: 20px;
          padding: 40px 30px;
          box-shadow: 0 10px 40px rgba(10, 36, 88, 0.08);

          height: 100%;
          display: flex;
          flex-direction: column;

          transition: transform 0.3s ease;
        }

        .testimonial-card:hover {
          transform: translateY(-8px);
        }

        .testimonial-rating {
          color: #ffd700;
          font-size: 1.5rem;
          margin-bottom: 20px;
        }

        .star {
          margin-right: 2px;
        }

        .testimonial-quote {
          font-size: 1.125rem;
          color: #0a2458;
          line-height: 1.6;
          font-style: italic;
          margin-bottom: 24px;
          
         
        }

        .testimonial-quote::before {
          content: '"';
          font-size: 3rem;
          color: #e2e8f0;
          position: absolute;
          top: -20px;
          left: -10px;
          font-family: Georgia, serif;
        }

        .testimonial-footer {
        margin-top: auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 20px;
          border-top: 1px solid #e2e8f0;
        }

       .testimonial-author {
  display: flex;
  align-items: center;   /* vertical center */
  {/* gap: 15px;             /* space between avatar & text */ */}
}


        .author-avatar {
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 1.25rem;
        }

        .author-info {
          {/* display: flex;
          flex-direction: column; */}
          line-height: 1.4;
        }

        .author-name {
          font-size: 1rem;
          color: #0a2458;
          margin: 0;
          font-weight: 600;
        }

        .author-company {
          font-size: 0.875rem;
          color: #0a2458;
          margin: 0;
        }

        .testimonial-service {
          background: rgba(10, 36, 88, 0.1);
          color: #0a2458;
          padding: 6px 12px;
          border-radius: 50px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .testimonials-cta {
          text-align: center;
        }

        .view-case-studies {
          background: transparent;
          color: #0a2458;
          border: 2px solid #0a2458;
          padding: 16px 32px;
          font-size: 1.125rem;
          font-weight: 600;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .view-case-studies:hover {
          background: #0a2458;
          color: white;
        }

        /* CTA Section */
        .services-cta {
          background: linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%);
          padding: 100px 0;
          position: relative;
          overflow: hidden;
        }

        .services-cta::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background:
            radial-gradient(
              circle at 20% 80%,
              rgba(255, 215, 0, 0.1) 0%,
              transparent 50%
            ),
            radial-gradient(
              circle at 80% 20%,
              rgba(255, 255, 255, 0.05) 0%,
              transparent 50%
            );
        }

        .cta-content {
          max-width: 800px;
          margin: 0 auto;
          text-align: center;
          position: relative;
          z-index: 1;
        }

        .cta-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(10px);
          padding: 8px 20px;
          border-radius: 50px;
          margin-bottom: 24px;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .cta-title {
          font-size: clamp(2rem, 5vw, 2.75rem);
          color: white;
          margin-bottom: 20px;
          font-weight: 700;
          line-height: 1.1;
        }

        .cta-subtitle {
          font-size: 1.25rem;
          color: rgba(255, 255, 255, 0.9);
          line-height: 1.6;
          max-width: 600px;
          margin: 0 auto 40px;
        }

        .cta-buttons {
          display: flex;
          gap: 20px;
          justify-content: center;
          flex-wrap: wrap;
          margin-bottom: 60px;
        }

        .cta-primary {
          background: #ffd700;
          color: #0a2458;
          border: none;
          padding: 20px 40px;
          font-size: 1.125rem;
          font-weight: 600;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .cta-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 15px 30px rgba(255, 215, 0, 0.3);
        }

        .cta-secondary {
          background: transparent;
          color: white;
          border: 2px solid white;
          padding: 20px 40px;
          font-size: 1.125rem;
          font-weight: 600;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .cta-secondary:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .cta-contact {
          display: inline-block;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border-radius: 20px;
          padding: 30px 40px;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .contact-info {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .contact-item {
          display: flex;
          align-items: center;
          gap: 12px;
          color: white;
          font-size: 1.125rem;
        }

        .contact-icon {
          font-size: 24px;
        }

        .contact-text {
          font-weight: 500;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .hero-stats {
            gap: 20px;
          }

          .benefits-grid {
            grid-template-columns: 1fr;
          }

          .featured-service-card {
            margin: 0 -20px;
            border-radius: 0;
          }

          .all-services-grid {
            grid-template-columns: 1fr;
          }

          .advantages-grid {
            grid-template-columns: 1fr;
          }

          .testimonials-grid {
            grid-template-columns: 1fr;
          }

          .cta-buttons {
            flex-direction: column;
            align-items: center;
          }

          .cta-primary,
          .cta-secondary {
            width: 100%;
            justify-content: center;
          }

          .contact-info {
            align-items: center;
            text-align: center;
          }

          .contact-item {
            flex-direction: column;
            gap: 8px;
          }
        }

        @media (max-width: 480px) {
          .services-hero {
            padding: 80px 0 60px;
          }

          .hero-title {
            font-size: 2rem;
          }

          .hero-subtitle {
            font-size: 1rem;
          }

          .stat-item {
            min-width: 100px;
          }

          .stat-value {
            font-size: 2rem;
          }

          .service-icon {
            width: 80px;
            height: 80px;
            font-size: 48px;
          }

          .service-title {
            font-size: 1.5rem;
          }

          .process-steps {
            gap: 20px;
          }

          .step-number {
            width: 60px;
            height: 60px;
            font-size: 1.5rem;
          }

          
        }
      `}</style>
    </div>
  );
}
